import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import SiteLayout from '@/components/feature/SiteLayout';
import Breadcrumb from '@/components/feature/Breadcrumb';
import MovieCard from '@/components/feature/MovieCard';
import { useSeo } from '@/hooks/useSeo';
import { useFavorites } from '@/hooks/useFavorites';
import { getMovieById, relatedMovies, typeLabel } from '@/utils/media';

export default function DetailPage() {
  const { id = '' } = useParams();
  const movie = getMovieById(id);
  const { isFavorite, toggle } = useFavorites();
  const [openEpisode, setOpenEpisode] = useState<string | null>(null);
  const [toast, setToast] = useState('');

  useSeo({
    title: movie ? `${movie.title} - 影片详情 - 91爱爱` : '影视资源不存在 - 91爱爱',
    description: movie
      ? `${movie.title}（${movie.year}）${movie.genres.join('、')}，${movie.summary.slice(0, 60)}`
      : '未找到对应的影视资源',
    keywords: movie ? `91爱爱,${movie.title},${movie.genres.join(',')},影视详情` : '91爱爱,影视详情',
  });

  const showToast = (text: string) => {
    setToast(text);
    window.setTimeout(() => setToast(''), 2000);
  };

  if (!movie) {
    return (
      <SiteLayout>
        <div className="mx-auto flex max-w-[1280px] flex-col items-center justify-center px-4 py-24 md:px-6">
          <i className="ri-file-unknow-line text-6xl text-foreground-300" />
          <h1 className="mt-5 font-heading text-xl font-bold text-foreground-900">
            影视资源不存在或已下架
          </h1>
          <p className="mt-2 text-sm text-foreground-500">你访问的资源可能已被移除，或链接有误。</p>
          <div className="mt-6 flex flex-wrap justify-center gap-3">
            <Link
              to="/category/hot"
              className="flex h-10 items-center gap-2 whitespace-nowrap rounded-md bg-primary-600 px-5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-700 cursor-pointer"
            >
              <i className="ri-apps-2-line" />
              浏览其他资源
            </Link>
            <Link
              to="/"
              className="flex h-10 items-center gap-2 whitespace-nowrap rounded-md border border-background-300 px-5 text-sm font-medium text-foreground-700 transition-colors hover:border-primary-300 cursor-pointer"
            >
              返回首页
            </Link>
          </div>
        </div>
      </SiteLayout>
    );
  }

  const favorited = isFavorite(movie.id);
  const related = relatedMovies(movie, 4);

  const handleShare = async () => {
    const url = window.location.href;
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(url);
      }
      showToast('链接已复制到剪贴板');
    } catch {
      showToast(`分享链接：${url}`);
    }
  };

  return (
    <SiteLayout>
      <div className="mx-auto max-w-[1280px] px-4 pt-6 md:px-6">
        <Breadcrumb
          items={[
            { label: typeLabel(movie.type), to: `/category/${movie.type}` },
            { label: movie.title },
          ]}
        />
      </div>

      <div className="mx-auto max-w-[1280px] px-4 py-6 md:px-6">
        <div className="grid gap-8 lg:grid-cols-[320px_1fr]">
          <div className="mx-auto w-full max-w-[280px] lg:mx-0 lg:max-w-none">
            <div className="relative aspect-[2/3] w-full overflow-hidden rounded-lg border border-background-200 bg-background-200">
              <img
                src={movie.cover}
                alt={`${movie.title}封面`}
                title={`${movie.title} - 91爱爱影视详情`}
                className="absolute inset-0 h-full w-full object-cover object-top"
              />
              <span className="absolute right-3 top-3 flex items-center gap-1 rounded-md bg-accent-500 px-2.5 py-1 text-sm font-semibold text-background-50">
                <i className="ri-star-fill text-xs" />
                {movie.rating.toFixed(1)}
              </span>
            </div>

            <div className="mt-4 flex gap-3">
              <button
                type="button"
                onClick={() => {
                  toggle(movie.id);
                  showToast(favorited ? '已取消收藏' : '已加入收藏');
                }}
                className={`flex h-11 flex-1 items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-colors cursor-pointer ${
                  favorited
                    ? 'bg-accent-500 text-background-50 hover:bg-accent-600'
                    : 'border border-primary-300 bg-primary-50 text-primary-700 hover:bg-primary-100'
                }`}
              >
                <i className={favorited ? 'ri-heart-fill' : 'ri-heart-line'} />
                {favorited ? '已收藏' : '收藏'}
              </button>
              <button
                type="button"
                onClick={handleShare}
                className="flex h-11 flex-1 items-center justify-center gap-2 whitespace-nowrap rounded-md border border-background-300 bg-background-50 text-sm font-medium text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-600 cursor-pointer"
              >
                <i className="ri-share-forward-line" />
                分享
              </button>
            </div>
          </div>

          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <span className="rounded-md bg-secondary-100 px-2 py-0.5 text-xs text-secondary-800">
                {typeLabel(movie.type)}
              </span>
              {movie.genres.map((genre) => (
                <Link
                  key={genre}
                  to={`/search?q=${encodeURIComponent(genre)}`}
                  className="rounded-md bg-primary-50 px-2 py-0.5 text-xs text-primary-700 transition-colors hover:bg-primary-100 cursor-pointer"
                >
                  {genre}
                </Link>
              ))}
            </div>

            <h1 className="mt-3 font-heading text-2xl font-bold text-foreground-950 md:text-4xl">
              {movie.title}
            </h1>
            <p className="mt-2 text-sm text-foreground-500 md:text-base">{movie.subtitle}</p>

            <dl className="mt-6 grid grid-cols-2 gap-x-6 gap-y-4 rounded-lg border border-background-200 bg-background-50 p-5 sm:grid-cols-3">
              <div>
                <dt className="text-xs text-foreground-400">导演</dt>
                <dd className="mt-1 text-sm font-medium text-foreground-900">{movie.director}</dd>
              </div>
              <div>
                <dt className="text-xs text-foreground-400">上映时间</dt>
                <dd className="mt-1 text-sm font-medium text-foreground-900">{movie.year} 年</dd>
              </div>
              <div>
                <dt className="text-xs text-foreground-400">地区</dt>
                <dd className="mt-1 text-sm font-medium text-foreground-900">{movie.region}</dd>
              </div>
              <div>
                <dt className="text-xs text-foreground-400">时长 / 集数</dt>
                <dd className="mt-1 text-sm font-medium text-foreground-900">{movie.duration}</dd>
              </div>
              <div>
                <dt className="text-xs text-foreground-400">更新时间</dt>
                <dd className="mt-1 text-sm font-medium text-foreground-900">{movie.updatedAt}</dd>
              </div>
              <div>
                <dt className="text-xs text-foreground-400">评分（示例）</dt>
                <dd className="mt-1 text-sm font-medium text-accent-600">{movie.rating.toFixed(1)}</dd>
              </div>
              <div className="col-span-2 sm:col-span-3">
                <dt className="text-xs text-foreground-400">主演</dt>
                <dd className="mt-1 text-sm font-medium text-foreground-900">{movie.cast.join(' / ')}</dd>
              </div>
            </dl>

            <section className="mt-6">
              <h2 className="flex items-center gap-2 font-heading text-lg font-bold text-foreground-950">
                <i className="ri-file-text-line text-primary-600" />
                内容简介
              </h2>
              <p className="mt-3 text-sm leading-relaxed text-foreground-600 md:text-base">
                {movie.summary}
              </p>
            </section>

            <div className="mt-5 flex items-start gap-3 rounded-lg border border-accent-200 bg-accent-50 p-4">
              <i className="ri-error-warning-line mt-0.5 text-lg text-accent-600" />
              <p className="text-xs leading-relaxed text-accent-900">
                本站为演示站点，暂无播放接口支持。以下章节列表仅用于展示剧集信息结构，不提供在线播放、下载等功能。
              </p>
            </div>

            {movie.type === 'tv' && movie.episodes && movie.episodes.length > 0 ? (
              <section className="mt-8">
                <h2 className="flex items-center gap-2 font-heading text-lg font-bold text-foreground-950">
                  <i className="ri-list-check-2 text-primary-600" />
                  剧集列表
                  <span className="text-xs font-normal text-foreground-400">
                    共 {movie.episodes.length} 集（演示）
                  </span>
                </h2>
                <div className="mt-4 overflow-hidden rounded-lg border border-background-200">
                  {movie.episodes.map((episode) => {
                    const open = openEpisode === episode.id;
                    return (
                      <div key={episode.id} className="border-b border-background-200 last:border-b-0">
                        <button
                          type="button"
                          onClick={() => setOpenEpisode(open ? null : episode.id)}
                          className="flex w-full items-center justify-between gap-3 bg-background-50 px-4 py-3 text-left transition-colors hover:bg-background-100 cursor-pointer"
                        >
                          <span className="flex items-center gap-3">
                            <span className="flex h-8 w-8 items-center justify-center rounded-md bg-primary-50 text-primary-600">
                              <i className="ri-play-circle-line" />
                            </span>
                            <span className="text-sm font-medium text-foreground-900">{episode.title}</span>
                          </span>
                          <i
                            className={`${open ? 'ri-subtract-line' : 'ri-add-line'} text-foreground-400`}
                          />
                        </button>
                        {open && (
                          <div className="animate-fade-in bg-background-100/60 px-4 py-3 pl-14">
                            <p className="text-sm leading-relaxed text-foreground-600">{episode.desc}</p>
                            <p className="mt-2 text-xs text-foreground-400">
                              <i className="ri-information-line mr-1" />
                              该剧集暂无播放接口，此处为静态演示内容
                            </p>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </section>
            ) : null}
          </div>
        </div>

        {related.length > 0 && (
          <section className="mt-12">
            <div className="mb-5 flex items-center justify-between border-b border-background-200 pb-3">
              <h2 className="flex items-center gap-2 font-heading text-lg font-bold text-foreground-950">
                <i className="ri-bookmark-3-line text-primary-600" />
                {movie.type === 'tv' ? '相关剧集推荐' : '相关作品推荐'}
              </h2>
              <Link
                to={`/category/${movie.type}`}
                className="flex items-center gap-1 whitespace-nowrap text-xs font-medium text-primary-600 transition-colors hover:text-accent-600 cursor-pointer"
              >
                查看更多
                <i className="ri-arrow-right-s-line" />
              </Link>
            </div>
            <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 xl:grid-cols-4">
              {related.map((item) => (
                <MovieCard key={item.id} movie={item} />
              ))}
            </div>
          </section>
        )}
      </div>

      {toast && (
        <div className="fixed bottom-8 left-1/2 z-50 -translate-x-1/2 animate-fade-up rounded-full bg-foreground-950 px-5 py-2.5 text-sm text-background-50">
          {toast}
        </div>
      )}
    </SiteLayout>
  );
}